"use client";

import { useT } from "@/lib/i18n/context";
import type { WBlogPopularPostsContent } from "@/lib/api/cms-window-content";
import { ImagePlaceholder } from "@/components/ui/ImagePlaceholder/ImagePlaceholder";
import { wBlogPopularPostsTranslations } from "./WBlogPopularPosts.i18n";
import styles from "./WBlogPopularPosts.module.css";

/**
 * WBlogPopularPosts - widget najpopularniejszych wpisow bloga (`w_blog_popular_posts`).
 *
 * Sortowanie po `count_view DESC` (backend uzywa `IMODEL_C_BLOG::getForApi`
 * z trybem `'popular'`). Uklad: pionowa lista kompaktowych poziomych kart
 * (miniaturka + tytul + CTA "czytaj wiecej"). Typowo w sidebarze - na main
 * area dziala jako gestsza alternatywa dla mozaikowego `WLatestBlogPosts`.
 *
 * Responsywne obrazy: `image_phone` przy <=480px, `image_tablet` przy <=1024px,
 * `image` jako desktop fallback (parytet z `WLatestBlogPosts`).
 */
export function WBlogPopularPosts({ content, title }: WBlogPopularPostsProps) {
  const t = useT(wBlogPopularPostsTranslations);

  if (!content) return null;

  return (
    <section className={styles.root}>
      <div className={styles.inner}>
        <h2 className={styles.heading}>{title}</h2>

        {content.items.length === 0 ? (
          <p className={styles.empty}>{t.empty}</p>
        ) : (
          <ul className={styles.list}>
            {content.items.map((item) => (
              <li key={item.id} className={styles.item}>
                <a href={item.href} className={styles.card} aria-label={item.name}>
                  <span className={styles.thumb}>
                    {item.image ? (
                      <picture>
                        {item.image_phone && (
                          <source srcSet={item.image_phone} media="(max-width: 480px)" />
                        )}
                        {item.image_tablet && (
                          <source srcSet={item.image_tablet} media="(max-width: 1024px)" />
                        )}
                        <img src={item.image} alt="" loading="lazy" />
                      </picture>
                    ) : (
                      <ImagePlaceholder />
                    )}
                  </span>
                  <span className={styles.body}>
                    <span className={styles.title}>{item.name}</span>
                    <span className={styles.readMore}>
                      {t.readMore} <span aria-hidden="true">&#8250;</span>
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

export type WBlogPopularPostsProps = {
  content: WBlogPopularPostsContent | null;
  title: string;
};
